import { useMemo, useState } from "react"
import { observer } from "mobx-react-lite"
import { useAccount, useConfig } from "wagmi"

import type { ILock } from "@/domains/lock"
import type { LockDisplayData, LockTableRowData } from "@/domains/lock-page/components/card/0.story-mocks"
import { TooltipProvider } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { mergeNftInteraction, resetNftInteraction } from "@/domains/interactions"
import { MergeNftActionsAdapter } from "@/domains/interactions/merge-nft/merge-nft-actions-adapter"
import { ResetNftActionsAdapter } from "@/domains/interactions/reset-nft/reset-nft-actions-adapter"
import { SortBy } from "@/domains/lock-page"
import { makeBreadcrumbs } from "@/domains/lock-page/breadcrumbs"
import { MergeCard } from "@/domains/lock-page/components/card/merge-card/components/merge-card"
import { LockPageLayout } from "@/domains/lock-page/components/lock-page-layout"
import { LockPageTabs } from "@/domains/lock-page/components/lock-page-tabs"
import { MANAGE_LOCK_TABS } from "@/domains/lock-page/constants"
import { ComplexTab } from "@/domains/navigation/components/complex-breadcrumb-dropdown"
import { TableProcessor } from "@/domains/table-processor"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useGovernanceToken } from "@/hooks/use-governance-token"
import { useSyncLockIdFromRouter } from "@/hooks/use-sync-lock-id-from-router"
import { useVestedToken } from "@/hooks/use-vested-token"
import useEventEmitter from "@/hooks/useEventEmitter"

function convertLockStoreToLockDisplayData(lock: ILock): LockDisplayData {
  return {
    id: lock.id,
    lockAmount: lock.lockAmount,
    lockValue: lock.lockValue,
    lockEnds: lock.lockEnds,
    isScalest: lock.isScalest,
    hasExpired: lock.hasExpired,
    displayLockAmount: lock.displayLockAmount,
    displayLockValue: lock.displayLockValue,
    displayLockEnds: lock.displayLockEnds,
  }
}

function convertLockStoreToLockTableRowData(lock: ILock): LockTableRowData {
  return {
    id: lock.id,
    lockAmount: lock.lockAmount,
    lockValue: lock.lockValue,
    lockEnds: lock.lockEnds,
    hasExpired: lock.hasExpired,
    hasVotedThisEpoch: lock.hasVotedThisEpoch,
    isReset: lock.isReset,
    displayLockAmount: lock.displayLockAmount,
    displayLockValue: lock.displayLockValue,
    displayLockEnds: lock.displayLockEnds,
  }
}

export default observer(() => {
  const scaleApp = useScaleApplication()
  const account = useAccount()
  const config = useConfig()
  const chainConfig = useChainConfig()
  const emitter = useEventEmitter()
  const vestedToken = useVestedToken()
  const governanceToken = useGovernanceToken()
  const breadcrumbs = makeBreadcrumbs()

  useSyncLockIdFromRouter()

  const [selectedNftId, setSelectedNftId] = useState<number | null>(null)

  const nft = scaleApp.Pages.Lock.selectedLock
  const nftId = nft?.id
  const allOtherNFTs = nftId ? scaleApp.Lock.Repository.getOthers(nftId) : []
  // Filter out expired NFTs as they cannot be merged
  const availableNFTs = allOtherNFTs.filter((lock) => !lock.hasExpired)
  const selectedNft = scaleApp.Lock.Repository.find(selectedNftId ?? 0)

  const onMergeNftClick = async (nft: ILock, mergeNft: ILock) => {
    if (!account.address) return

    // only reset items that need it
    if (!mergeNft.isReset) {
      const resetOnChainActions = new ResetNftActionsAdapter()
      const resetResult = await resetNftInteraction({
        account: account.address,
        chainId: chainConfig.chainId,
        config,
        tokenId: mergeNft.id,
        voterAddress: chainConfig.equalizer.contracts.voter,
        emitter,
        eventBus: scaleApp.EventBus,
        onChainActions: resetOnChainActions,
      })

      if (!resetResult || !resetResult.success) {
        emitter.emit("error-reset-nft", {
          title: "Error resetting NFT",
          description: `Unable to reset NFT #${mergeNft.id}`,
          variant: "error",
        })
        return
      }
    }

    const onChainActions = new MergeNftActionsAdapter()

    const result = await mergeNftInteraction({
      account: account.address,
      chainId: chainConfig.chainId,
      config,
      vestedAddress: vestedToken.address,
      fromTokenId: mergeNft.id,
      toTokenId: nft.id,
      emitter,
      eventBus: scaleApp.EventBus,
      onChainActions,
    })

    return result
  }

  const canMerge = nft && selectedNft && !nft.hasExpired

  const potentialMergeNft = useMemo(() => {
    if (!nft) {
      return scaleApp.Lock.Factory.createDefault()
    }

    if (!selectedNft) {
      return scaleApp.Lock.Factory.createFromNft(nft)
    }

    const newLockAmountTotal = Number(selectedNft.lockAmount) + Number(nft.lockAmount)
    const newLockEnds = Math.max(selectedNft.lockEnds, nft.lockEnds)
    const currentNftPower = Number(nft.lockAmount) > 0 ? Number(nft.lockValue) / Number(nft.lockAmount) : 0
    const incomingNftPower =
      Number(selectedNft.lockAmount) > 0 ? Number(selectedNft.lockValue) / Number(selectedNft.lockAmount) : 0
    const newPowerRatio = nft.lockEnds > selectedNft.lockEnds ? currentNftPower : incomingNftPower

    const lockValue = Number(newLockAmountTotal) * newPowerRatio

    return scaleApp.Lock.Factory.createFromNft({
      id: nft.id,
      lockAmount: newLockAmountTotal,
      lockEnds: newLockEnds,
      lockValue,
    })
  }, [nft, selectedNft, scaleApp.Lock.Factory])

  const sortBy = scaleApp.Pages.Lock.sortBy
  const setSortBy = (value: SortBy) => scaleApp.Pages.Lock.setSortByFromUi(value)

  const processed = TableProcessor({
    rows: availableNFTs,
    sort: sortBy,
  })

  // Pass ILock array directly - TypeScript ensures type safety via Pick<ILock>
  const processedTableRows: Array<LockTableRowData> = useMemo(() => {
    return processed.rows as Array<LockTableRowData>
  }, [processed.rows])

  const complexTab: ComplexTab | undefined = MANAGE_LOCK_TABS[2]

  if (!complexTab) {
    return null
  }

  const title = `Merge`

  // Pass ILock directly - TypeScript ensures type safety via Pick<ILock>
  const currentLockDisplayData = nft
  const futureLockDisplayData = potentialMergeNft

  const hasExpiryIncrease = potentialMergeNft.lockEnds > (nft?.lockEnds ?? 0)
  const showClaimRewardsWarning = !!selectedNft && selectedNft.hasRewardsToClaim

  return (
    <PageContainer>
      <MetaData title={title} />
      <PageTitle title={title} items={breadcrumbs} />
      <TooltipProvider>
        <LockPageLayout>
          <LockPageTabs />
          {currentLockDisplayData && nft && nftId !== undefined && (
            <MergeCard
              nftId={nftId}
              currentLock={currentLockDisplayData}
              futureLock={futureLockDisplayData}
              selectedNftId={selectedNftId}
              allOtherNFTs={processedTableRows}
              hasExpiryIncrease={hasExpiryIncrease}
              showClaimRewardsWarning={showClaimRewardsWarning}
              hasExpired={nft.hasExpired}
              canMerge={canMerge ?? false}
              icon={complexTab.icon ? <complexTab.icon /> : undefined}
              governanceToken={governanceToken}
              vestedTokenSymbol={vestedToken.symbol}
              onSelectNft={(id) => setSelectedNftId(id)}
              onDeselectNft={() => setSelectedNftId(null)}
              onMerge={() => {
                if (!nft || !selectedNft) return
                void onMergeNftClick(nft, selectedNft)
              }}
              sortBy={sortBy}
              onSortChange={setSortBy}
            />
          )}
        </LockPageLayout>
      </TooltipProvider>
    </PageContainer>
  )
})
